import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { Location } from '@angular/common';
import { Product, Quotes } from '@models/models';
import { MessageService } from '@services/helpers/message-service/message.service';
import { StringModifierServiceProvider } from '@services/helpers/string-modifier-service/stringModifier.service';
import { QuotesDataProvider } from '@services/quote/quotes-data-service/quotes.data.service';
import { InvoicesDataProvider } from '@services/invoice/invoices-data-service/invoices.data.service';
import { JobsDataServiceProvider } from '@services/job/jobs.data.service';
import { UserServiceProvider } from '@services/user/user.service';
import { TemplatesDataProvider } from '@services/data-services/templates-data-service/templates.data.service';
import { AccountDataServiceProvider } from '@services/user/account.data.service';
import { QuoteUpdateService } from '@services/quote/quote-update.service';
import { FileUploadDataProvider } from '@services/files/file-upload.data.service';
import { FileDownloadDataServiceProvider } from '@services/files/file-download-service/file-download.data.service';
import { PaymentsServiceProvider } from '@services/payments/payments-service/payments.service';

@Component({
	selector: 'quote-view',
	template: require('./quote-view.html'),
	styles: [require('./quote-view.scss')]
})
export class QuoteViewComponent implements OnInit {
	@Input('quoteId') quoteId: number;
	@Input('jobId') jobId: number;
	@Input('leadId') leadId: number;
	@Output() showSendModal: EventEmitter<any> = new EventEmitter<any>();
	data: Quotes;
	userCurrencySymbol: string;
	isJobView: boolean;
	isLeadView: boolean;
	isArchived: boolean;
	linkHref: string;
	imgSrc: string;
	choosenEmail: any;
	sendMessageData: any;
	company: any;
	packages: Array<Product>;
	addons: Array<Product>;
	discount: Array<Product> = [];
	actions: any;
	packageForModal: Product;
	chosenProducts: Product[];
	isFixed = false;
	autoGenerateInvoice: boolean;

	constructor(
		private quoteDataService: QuotesDataProvider,
		private messageService: MessageService,
		private location: Location,
		private invoicesDataService: InvoicesDataProvider,
		private jobsDataService: JobsDataServiceProvider,
		private userService: UserServiceProvider,
		private templateRequestsService: TemplatesDataProvider,
		private accountService: AccountDataServiceProvider,
		private stringModifierService: StringModifierServiceProvider,
		private fileUploadService: FileUploadDataProvider,
		private fileDownloadService: FileDownloadDataServiceProvider
	) {}

	ngOnInit(): void {
		this.configureCommunicationAdapter();
		if (this.quoteId && this.jobId) {
			this.getQuoteSourceData();
		} else if (this.quoteId && this.leadId) {
			this.getJobId();
		}
		this.setQuoteViewLinks();
		this.userCurrencySymbol = this.userService.userCurrencySymbol;
		this.accountService.getCompany().subscribe(
			(data): void => {
				this.company = data.company;
			},
			(error): void => {
				this.messageService.showDefaultErrorMessage(error);
			}
		);
	}

	deleteModalQuote(): void {
		if (this.isArchived) {
			this.messageService.showToaster('e_6', 'lead');
		} else {
			$('#deleteQuoteModal').modal();
		}
	}

	openGenerateInvoiceFromQuoteModal(): void {
		$('#generateInvoiceModal').modal('show');
	}

	openMarkQuoteAsAcceptedModal(): void {
		$('#markQuoteAsAcceptedModal').modal('show');
	}

	updateBtnActions(id: number): void {
		this.quoteDataService
			.updateQuoteActions(this.jobId, this.quoteId)
			.subscribe(
				(actions): void => this.successUpdateBtnActions(actions, id),
				(error): void => this.messageService.showDefaultErrorMessage(error)
			);
	}

	private successUpdateBtnActions(actions, id: number): void {
		this.actions = actions;
		this.redirectToInvoice(id);
	}

	private redirectToInvoice(invoiceId: number): void {
		this.location.go(`/invoices/${invoiceId}`);
	}

	getImgStatusSrc(quoteStatus) {
		this.imgSrc = 'https://app.studioninja.co/build/release-pipeline-10/resources/dist/assets/images/icons/statuses/';
		if (quoteStatus === 'ACCEPTED') {
			this.imgSrc += 'accepted.png';
		} else if (quoteStatus === 'DECLINED') {
			this.imgSrc += 'declined.png';
		} else if (quoteStatus === 'SENT') {
			this.imgSrc += 'sent.png';
		} else if (quoteStatus === 'NOT_SENT') {
			this.imgSrc += 'notsent.png';
		}
		return this.imgSrc;
	}

	showSendQuote() {
		this.showSendModal.emit({
			email: this.data.job.primaryContact.email,
			jobId: this.jobId
		});
	}

	showDetailsModal(product: Product): void {
		this.packageForModal = product;
		$('#productDetailsModal').modal('show');
	}

	saveAsPdf(): void {
		this.fileDownloadService.downloadPdf(
			this.data.job.clientPortalLink,
			this.quoteId,
			'quote'
		);
	}

	checkAddProductsPhraseVisibility(): boolean {
		// this long check allows to show the phrase if: there are addons AND
		// there are no packages OR there is at least one NOT mandatory addon
		return (
			this.addons &&
			this.addons.length > 0 &&
			((this.packages && this.packages.length > 0) ||
				this.addons.some((addon: Product) => !addon.mandatory))
		);
	}

	private setQuoteViewLinks(): void {
		this.isJobView = this.location.path().includes('/jobs/');
		this.isLeadView = this.location.path().includes('/leads/');
		if (this.isJobView) {
			this.linkHref = `/jobs/${this.jobId}/quote/${this.quoteId}/update`;
		} else if (this.isLeadView) {
			this.linkHref = `leads/${this.leadId}/quote/${this.quoteId}/update`;
		}
	}

	private getQuoteSourceData(): void {
		this.quoteDataService.getQuoteToEditV1(this.jobId, this.quoteId).subscribe(
			(data): void => {
				this.successGetQuoteData(data);
			},
			(error): void => this.messageService.showDefaultErrorMessage(error)
		);
	}

	private successGetQuoteData(data): void {
		const {
			attachments,
			actions,
			quoteType,
			generateInvoiceWhenAccepted
		} = data;

		this.actions = actions;
		this.data = data;
		this.isFixed = quoteType === 'FIXED';
		this.autoGenerateInvoice = generateInvoiceWhenAccepted;
		if (this.data.summary !== null) {
			this.data.summary = Object.assign(data.summary, {
				taxesName: data.currencyAndTax.taxName
			});
		}
		this.setPackagesAndAddons(data.products);
		this.getImgStatusSrc(this.data.status);

		if (attachments && attachments.length) {
			this.fileUploadService.setAttachmentsFromServer(attachments);
		}
		this.isArchived = data.job.archived;
	}

	private getJobId(): void {
		this.quoteDataService.getJobId(this.leadId).subscribe(
			(data): void => {
				this.jobId = data;
				this.getQuoteSourceData();
			},
			(error): void => this.messageService.showDefaultErrorMessage(error)
		);
	}

	private setPackagesAndAddons(products): void {
		const sortedProducts = QuoteUpdateService.sortProducts(products);
		const {
			packages,
			addons,
			discounts
		} = QuoteUpdateService.setPackagesAndAddons(sortedProducts);
		this.packages = packages;
		this.addons = addons;
		this.discount = discounts;
		this.checkForSinglePackage();
		if (this.isFixed) {
			this.chosenProducts = this.addons.concat(this.discount);
		} else {
			this.chosenProducts = this.groupChosenProducts();
		}
	}

	private checkForSinglePackage(): void {
		if (this.packages.length === 1) {
			this.packages[0].isSingle = true;
		}
	}

	private groupChosenProducts(): Product[] {
		const chosenProducts = [];
		this.packages.forEach((packageItem: Product): void => {
			if (packageItem.chosen) {
				chosenProducts.push(packageItem);
			}
		});
		this.addons.forEach((addon: Product): void => {
			if (addon.chosen) {
				chosenProducts.push(addon);
			}
		});

		return chosenProducts;
	}

	private configureCommunicationAdapter(): void {
		window.snCommunicationAdapter.registerMethod(
			'updateQuoteViewData',
			this.getQuoteSourceData.bind(this)
		);
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=ls9lvp2s4pi!./web/modern/components/quote/quote-view/quote-view.ts